<template>
  <do-month-picker
    contenteditable="false"
    :value="lookBack"
    is-icon
    month-picker-menu-class="-left-40"
    @click.stop
    @context:change="handleContextChange"
  >
    <template #extra>
      <lf-input
        v-tooltip="$t('SCORECARDS.BUSINESS_ZIP_CODE_HINT')"
        name="value_input"
        data-cy="value-input"
        class="mx-2 mb-4 h-10"
        :placeholder="$t('COMMON.VALUE')"
        :value="attributeValue"
        no-margin
        @input.stop="updateValue"
        @keydown.stop="updateValue"
        @paste.stop="updateValue"
        @copy.stop
      />
    </template>
    <template #icon-activator="{ toggleMenu }">
      <slot :toggle-menu="toggleMenu">
        <icon-base
          :icon="context ? IconEdit : IconWarning"
          :icon-color="context ? UTIL_COLORS.DEFAULT : UTIL_COLORS.WARNING"
          class="cursor-pointer"
          data-cy="set-look-back-context"
          @click.stop="toggleMenu"
        />
      </slot>
    </template>
  </do-month-picker>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { DEFAULT_LOOKBACK_PERIOD, UTIL_COLORS } from "@/helpers/constants";
import { formatDateCustom, transformStringToArray } from "@/helpers/formatting";

import IconEdit from "@/components/icons/IconEdit.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import DoMonthPicker from "@/views/profile/components/dataOrchestration/configPanel/DOMonthPicker.vue";

import type {
  DataServiceContextValue,
  IContextSetValueModel,
  IRelativeDateRange,
  ISpecificDateRange,
  LookbackValue,
  RelativeLookbackRange,
} from "@/models/orchestration";

const emit = defineEmits<{
  (event: "apply", value: IContextSetValueModel): void;
}>();

const { context } = defineProps<{
  context?: IContextSetValueModel;
}>();

const attributeValue = ref("");
const attributeArrayValues = ref<string[]>([]);

const isRelativeLookbackRange = (
  value: DataServiceContextValue
): value is RelativeLookbackRange => {
  const options = ["lookback_months_from", "lookback_months_through"];
  return (
    !!value &&
    typeof value === "object" &&
    options.every((option) => option in value)
  );
};
const isSpecificDateRange = (
  value: DataServiceContextValue
): value is ISpecificDateRange => {
  const options = ["from", "through"];
  return (
    !!value &&
    typeof value === "object" &&
    options.every((option) => option in value)
  );
};
const isRelativeDateRange = (
  value: DataServiceContextValue
): value is IRelativeDateRange => {
  return !!value && typeof value === "object" && "lookback_months" in value;
};
const getRelativeLookbackRange = (
  value: RelativeLookbackRange
): [number, number] => {
  return [value.lookback_months_from, value.lookback_months_through];
};
const getSpecificDateRange = (value: ISpecificDateRange): [string, string] => {
  return [value.from, value.through];
};
const getRelativeDateRange = (value: IRelativeDateRange): [number] => {
  return [value.lookback_months];
};
const lookBack = computed(() => {
  if (!context?.value) {
    return DEFAULT_LOOKBACK_PERIOD;
  }
  const contextValue = context.value;

  if (isRelativeLookbackRange(contextValue)) {
    return getRelativeLookbackRange(contextValue);
  }

  if (isSpecificDateRange(contextValue)) {
    return getSpecificDateRange(contextValue);
  }

  if (isRelativeDateRange(contextValue)) {
    return getRelativeDateRange(contextValue);
  }

  return DEFAULT_LOOKBACK_PERIOD;
});

const getDateContext = (
  data: LookbackValue
): IContextSetValueModel | undefined => {
  if (data.type === "relative_lookback_range") {
    if (data.lookback.length === 1) {
      return {
        type: "in_array_values",
        value: {
          lookback_months_from: Number(data.lookback[0]),
          lookback_months_through: Number(data.lookback[0]),
          values: attributeArrayValues.value,
        },
      };
    }

    return {
      type: "in_array_values",
      value: {
        lookback_months_from: Number(data.lookback[0]),
        lookback_months_through: Number(data.lookback[1]),
        values: attributeArrayValues.value,
      },
    };
  }
  if (data.lookback.length === 2) {
    return {
      type: "in_array_values",
      value: {
        from: formatDateCustom(data.lookback[0] as string, "yyyy-MM-dd"),
        through: formatDateCustom(data.lookback[1] as string, "yyyy-MM-dd"),
        values: attributeArrayValues.value,
      },
    };
  } else if (data.lookback.length === 1) {
    return {
      type: "in_array_values",
      value: {
        lookback_months: Number(data.lookback[0]),
        include_month_to_date: data.hasMonthToDate,
        values: attributeArrayValues.value,
      },
    };
  }
};
const handleContextChange = (data: LookbackValue) => {
  if (data.lookback[0] === null) {
    return;
  }
  const context = getDateContext(data);
  if (context) {
    emit("apply", context);
  }
};

const updateValue = (event: Event) => {
  const value = (event.target as HTMLInputElement)?.value;

  if (value === undefined) {
    return;
  }

  attributeValue.value = value;

  attributeArrayValues.value = transformStringToArray(attributeValue.value);
};

onMounted(() => {
  const { values } = context?.value || {};
  if (values?.length) {
    attributeArrayValues.value = values;
    attributeValue.value = values.join(",");
  }
});
</script>
